import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  ReactNode,
} from "react";
const { VITE_WEBSOCKET_URL } = import.meta.env;

// Step 1: Define the WebSocket context type
type WebSocketContextType = {
  ws: WebSocket | null;
  setRefetch: (refetch: () => void) => void; // Function to set the refetch callback
  sendMessage: (message: string) => void; // Function to send messages
};

// Step 2: Create the context
const WebSocketContext = createContext<WebSocketContextType | null>(null);

// Step 3: Create the WebSocket provider component with automatic reconnection
export const WebSocketProvider = ({ children }: { children: ReactNode }) => {
  const ws = useRef<WebSocket | null>(null);
  const refetchRef = useRef<(() => void) | null>(null); // Store the refetch function
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Store the reconnect timeout reference

  const reconnectWebSocket = () => {
    if (ws.current) return; // Don't reconnect if WebSocket is still active

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current); // Clear the previous timeout if it exists
    }

    console.log('WebSocket disconnected, attempting to reconnect in 5 seconds...');
    // Wait for a brief period before attempting reconnection
    reconnectTimeoutRef.current = setTimeout(() => {
      console.log('Attempting to reconnect WebSocket...');
      ws.current = new WebSocket(`${VITE_WEBSOCKET_URL}`);
      setupWebSocket();
    }, 5000); // Try reconnecting after 5 seconds
  };

  const setupWebSocket = () => {
    if (!ws.current) return;

    ws.current.onopen = () => {
      console.log('WebSocket connection established successfully!');
    };

    ws.current.onmessage = (event) => {
      console.log('WebSocket message received:', event.data);
      if (refetchRef.current) {
        refetchRef.current();
      }
    };

    ws.current.onerror = (error) => {
      console.error('WebSocket error occurred:', error);
      reconnectWebSocket();
    };

    ws.current.onclose = (event) => {
      console.log(`WebSocket connection closed. Code: ${event.code}, Reason: ${event.reason}`);
      reconnectWebSocket();
    };
  };

  useEffect(() => {
    if (!ws.current) {
      console.log('Initializing WebSocket connection to:', VITE_WEBSOCKET_URL);
      ws.current = new WebSocket(`${VITE_WEBSOCKET_URL}`);
      setupWebSocket();
    }

    return () => {
      if (ws.current) {
        console.log('Cleaning up WebSocket connection');
        ws.current.close();
        ws.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  // Function to send messages
  const sendMessage = (message: string) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(message);
    } else {
      console.warn("WebSocket is not open. Message not sent:", message);
    }
  };
  // Function to set the refetch callback function
  const setRefetch = (refetch: () => void) => {
    refetchRef.current = refetch;
  };

  // Provide the WebSocket connection and refetch function to the rest of the app
  return (
    <WebSocketContext.Provider value={{ ws: ws.current, setRefetch,sendMessage }}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Step 4: Create the custom hook to access the WebSocket context
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};
